'use client'
import { usePathname } from 'next/navigation'
import { PageSettings } from './PageSettingsContext'

type RouteSettingsConfig = Array<{
  match: string // Exact match or route prefix (startsWith)
  settings: PageSettings
}>

/**
 * Normalizes a path by removing trailing slashes (except for root '/')
 */
const normalizePath = (path: string): string => {
  return path === '/' ? '/' : path.replace(/\/$/, '')
}

/**
 * Hook to generate page settings based on the current route
 * 
 * Route matching:
 * - Exact match: '/w47-deep-work-direct' matches exactly
 * - Prefix match: '/w47' matches any route starting with '/w47'
 * 
 * Matches are checked in order - first match wins.
 * Trailing slashes are normalized (removed) for consistent matching.
 */
export const usePageSettingsFromRoute = (): PageSettings => {
  const pathname = usePathname()
  const normalizedPathname = normalizePath(pathname)

  const routeSettings: RouteSettingsConfig = [
    {
      match: '/w47-deep-work-direct',
      settings: {
        enableHeroGoogleLogin: true,
        enableHeaderGoogleLogin: true,
        showGoogleLogoHeader: false,
        showGoogleLogoHero: true,
        enableTimeSavingsGoogleLogin: true,
        showGoogleLogoTimeSavings: true,
        enableAiModelsGoogleLogin: true,
        showGoogleLogoAiModels: true,
        enableAiChatFeatureGoogleLogin: true,
        showGoogleLogoAiChatFeature: true,
        enableDictationGoogleLogin: true,
        showGoogleLogoDictation: true,
        enableDictateAndImproveGoogleLogin: true,
        showGoogleLogoDictateAndImprove: true,
        enableTaskWriterGoogleLogin: true,
        showGoogleLogoTaskWriter: true,
        enableInboxGoogleLogin: true,
        showGoogleLogoInbox: true,
        enableHyperAIGoogleLogin: true,
        showGoogleLogoHyperAI: true,
        enablePricingGoogleLogin: true,
        showGoogleLogoPricing: true,
      },
    },
    {
      match: '/w45-deepwork-google',
      settings: {
        enableHeroGoogleLogin: true,
        enableHeaderGoogleLogin: true,
        showGoogleLogoHeader: false,
        showGoogleLogoHero: true,
        enableTimeSavingsGoogleLogin: true,
        showGoogleLogoTimeSavings: true,
        enableAiModelsGoogleLogin: true,
        showGoogleLogoAiModels: true,
        enableAiChatFeatureGoogleLogin: true,
        showGoogleLogoAiChatFeature: true,
        enableDictationGoogleLogin: true,
        showGoogleLogoDictation: true,
        enableDictateAndImproveGoogleLogin: true,
        showGoogleLogoDictateAndImprove: true,
        enableTaskWriterGoogleLogin: true,
        showGoogleLogoTaskWriter: true,
        enableInboxGoogleLogin: true,
        showGoogleLogoInbox: true,
        enableHyperAIGoogleLogin: true,
        showGoogleLogoHyperAI: true,
        enablePricingGoogleLogin: true,
        showGoogleLogoPricing: true,
      },
    },
    {
      match: '/htma-639',
      settings: {
        enableHeroGoogleLogin: true,
        enableHeaderGoogleLogin: true,
        showGoogleLogoHeader: false,
        showGoogleLogoHero: true,
        enableTimeSavingsGoogleLogin: true,
        showGoogleLogoTimeSavings: true,
        enableAiModelsGoogleLogin: true,
        showGoogleLogoAiModels: true,
        enableAiChatFeatureGoogleLogin: true,
        showGoogleLogoAiChatFeature: true,
        enableDictationGoogleLogin: true,
        showGoogleLogoDictation: true,
        enableDictateAndImproveGoogleLogin: true,
        showGoogleLogoDictateAndImprove: true,
        enableTaskWriterGoogleLogin: true,
        showGoogleLogoTaskWriter: true,
        enableInboxGoogleLogin: true,
        showGoogleLogoInbox: true,
        enableHyperAIGoogleLogin: true,
        showGoogleLogoHyperAI: true,
        enablePricingGoogleLogin: true,
        showGoogleLogoPricing: true,
      },
    },
    {
      match: '/w48-htma-email-signup-basic',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w48-htma-email-signup-lean',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'lean',
      },
    },
    {
      match: '/w48-htma-email-signup-instant',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/W50-instant-button',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/W50-instant-description',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w50-superhuman-meets-pm',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w50-inbox-zero-for-pm',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w50-slackless-pm',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w50-clickup-meets-superhuman',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w50-async-remote-teams',
      settings: {
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    // (w01-w50-instant-button-based) routes
    {
      match: '/w01-anti-slack-operating-system',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-do-pm-right',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-do-you-think-fast',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-endless-threads-end',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-keyboard-first-pm',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-one-thing-right',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-simple-pm-wins',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-slack-made-you-busy',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },
    {
      match: '/w01-work-lives-here',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'instant',
      },
    },


    // (w01-w50-async-remote-teams-based) routes
    {
      match: '/w01-do-pm-right-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w01-do-you-think-fast-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w01-keyboard-first-pm-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w01-simple-pm-wins-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w01-slack-made-you-busy-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: true,
        emailSignupVariant: 'basic',
      },
    },
    {
      match: '/w05-calendly-demo',
      settings: {
        showHeaderCTAs: false,
        enableHeroGoogleLogin: false,
        enableEmailSignup: false,
      },
    },
  ]

  // First, try exact matches (more specific)
  for (const config of routeSettings) {
    if (normalizedPathname === normalizePath(config.match)) {
      return config.settings
    }
  }

  // // Then try prefix matches (less specific)
  // for (const config of routeSettings) {
  //   if (pathname.startsWith(config.match)) {
  //     return config.settings
  //   }
  // }

  return {}
}
